import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../hooks/useAuth';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';

const InvoiceView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showEditForm, setShowEditForm] = useState(false);

  // Fetch invoice
  const { data: invoice, isLoading } = useQuery({
    queryKey: ['invoice', id],
    queryFn: async () => {
      const response = await api.get(`/invoices/${id}`);
      return response.data;
    },
  });

  // Send invoice mutation
  const sendMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/invoices/${id}/send`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });

  // Delete invoice mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await api.delete(`/invoices/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('invoices.deletedSuccess'),
        timer: 2000,
        showConfirmButton: false
      }).then(() => {
        navigate('/invoices');
      });
    },
    onError: (error) => {
      console.error('Invoice deletion error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || t('invoices.deletionFailed');
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: errorMessage,
        confirmButtonText: t('common.ok')
      });
    },
  });

  // Generate PDF mutation (using template filling)
  const pdfMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/invoices/${id}/pdf/fill-template`, {}, {
        responseType: 'blob',
      });
      return { data: response.data, filename: `invoice-${invoice?.invoiceNumber}.pdf` };
    },
    onSuccess: ({ data, filename }) => {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => {
      console.error('PDF generation error:', error);
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: error.response?.data?.message || t('invoices.pdfGenerationFailed'),
        timer: 3000,
        showConfirmButton: false
      });
    },
  });

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      void: 'bg-red-100 text-red-800',
    };
    return colors[status] || colors.draft;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="text-center py-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('invoices.notFound')}</h1>
        <Link to="/invoices" className="btn btn-primary mt-4">
          {t('common.backToInvoices')}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('invoices.invoice')} {invoice.invoiceNumber}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('invoices.createdOn')} {dayjs(invoice.createdAt).format('DD/MM/YYYY HH:mm')}
          </p>
        </div>
        <div className="flex space-x-3">
          <Link to="/invoices" className="btn btn-secondary">
            {t('common.backToInvoices')}
          </Link>
          <button
            onClick={() => setShowEditForm(!showEditForm)}
            className="btn btn-primary"
          >
            {showEditForm ? t('common.cancelEdit') : t('invoices.editInvoice')}
          </button>
        </div>
      </div>

      {/* Status and Actions */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(invoice.status)}`}>
              {t(`invoices.statuses.${invoice.status}`)}
            </span>
            <div className="text-sm text-gray-500">
              {t('invoices.invoiceDate')}: {dayjs(invoice.invoiceDate).format('DD/MM/YYYY')}
            </div>
            <div className="text-sm text-gray-500">
              {t('invoices.invoiceNumber')}: {invoice.invoiceNumber}
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => pdfMutation.mutate()}
              disabled={pdfMutation.isPending}
              className="btn btn-secondary btn-sm"
            >
              {pdfMutation.isPending ? t('common.loading') : t('invoices.generatePdf')}
            </button>
            {(invoice.status === 'draft' || invoice.status === 'sent') && (
              <button
                onClick={() => sendMutation.mutate()}
                disabled={sendMutation.isPending}
                className="btn btn-primary btn-sm"
              >
                {sendMutation.isPending ? t('common.loading') : t('invoices.sendInvoice')}
              </button>
            )}
            <button
              onClick={() => {
                Swal.fire({
                  title: t('invoices.confirmDelete'),
                  text: t('invoices.confirmDeleteText'),
                  icon: 'warning',
                  showCancelButton: true,
                  confirmButtonColor: '#d33',
                  cancelButtonColor: '#3085d6',
                  confirmButtonText: t('invoices.yesDelete'),
                  cancelButtonText: t('common.cancel')
                }).then((result) => {
                  if (result.isConfirmed) {
                    deleteMutation.mutate();
                  }
                });
              }}
              disabled={deleteMutation.isPending}
              className="btn btn-danger btn-sm"
            >
              {deleteMutation.isPending ? t('common.loading') : t('common.delete')}
            </button>
          </div>
        </div>
      </div>

      {/* Invoice Details */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Company Info */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('invoices.from')}</h3>
          <div className="space-y-2">
            <div className="font-medium">{invoice.companyId?.name}</div>
            {invoice.companyId?.address && (
              <div className="text-sm text-gray-600 whitespace-pre-line">
                {invoice.companyId.address}
              </div>
            )}
            {invoice.companyId?.phone && (
              <div className="text-sm text-gray-600">{t('common.phone')}: {invoice.companyId.phone}</div>
            )}
            {invoice.companyId?.taxNumber && (
              <div className="text-sm text-gray-600">{t('invoices.tax')}: {invoice.companyId.taxNumber}</div>
            )}
          </div>
        </div>

        {/* Customer Info */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('invoices.to')}</h3>
          <div className="space-y-2">
            <div className="font-medium">{invoice.customerId?.name}</div>
            {invoice.customerId?.address && (
              <div className="text-sm text-gray-600 whitespace-pre-line">
                {invoice.customerId.address}
              </div>
            )}
            {invoice.customerId?.email && (
              <div className="text-sm text-gray-600">{t('common.email')}: {invoice.customerId.email}</div>
            )}
            {invoice.customerId?.phone && (
              <div className="text-sm text-gray-600">{t('common.phone')}: {invoice.customerId.phone}</div>
            )}
            {invoice.customerId?.taxNumber && (
              <div className="text-sm text-gray-600">{t('invoices.tax')}: {invoice.customerId.taxNumber}</div>
            )}
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('invoices.items')}</h3>
        <div className="overflow-hidden">
          <table className="table">
            <thead>
              <tr>
                <th>{t('invoices.description')}</th>
                <th className="text-right">{t('invoices.quantity')}</th>
                <th className="text-right">{t('invoices.unitPrice')}</th>
                <th className="text-right">{t('invoices.vatRate')}</th>
                <th className="text-right">{t('invoices.lineTotal')}</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((item, index) => (
                <tr key={index}>
                  <td>{item.description}</td>
                  <td className="text-right">{item.qty}</td>
                  <td className="text-right">{formatCurrency(item.unitPrice, invoice.currency)}</td>
                  <td className="text-right">{item.vatRate}%</td>
                  <td className="text-right font-medium">
                    {formatCurrency(item.qty * item.unitPrice, invoice.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2">
                <td colSpan="4" className="text-right font-medium">
                  {t('common.subtotal')}:
                </td>
                <td className="text-right font-medium">
                  {formatCurrency(invoice.totals.subtotal, invoice.currency)}
                </td>
              </tr>
              <tr>
                <td colSpan="4" className="text-right font-medium">
                  {t('common.vat')}:
                </td>
                <td className="text-right font-medium">
                  {formatCurrency(invoice.totals.vatTotal, invoice.currency)}
                </td>
              </tr>
              <tr className="border-t-2 bg-gray-50">
                <td colSpan="4" className="text-right font-bold text-lg">
                  {t('common.total')}:
                </td>
                <td className="text-right font-bold text-lg">
                  {formatCurrency(invoice.totals.grandTotal, invoice.currency)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Notes */}
      {invoice.notes && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('invoices.notes')}</h3>
          <div className="text-gray-700 whitespace-pre-line">{invoice.notes}</div>
        </div>
      )}
    </div>
  );
};

export default InvoiceView;
